import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Palette, KeyRound, ShieldCheck, LogOut, UserCircle } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage, SUPPORTED_LANGUAGES } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import PasswordChangeDialog from '../PasswordChangeDialog';
import SecuritySettingsDialog from '../SecuritySettingsDialog';

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
};

// Benutzer-Menue der Shellbar (Profil, Theme, Sprache, Sicherheit, Logout).
export default function FioriUserMenu() {
  const { user, logout } = useContext(AuthContext);
  const { t, language, changeLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [showSec, setShowSec] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="fs-avatar" data-testid="fiori-user-menu-trigger" aria-label={t('fiori.userMenu', 'Benutzermenü')}>
            {getInitials(user?.full_name)}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[260px] z-[70]" data-testid="fiori-user-menu">
          <DropdownMenuLabel className="flex items-center gap-3 py-2">
            <span className="fs-avatar" style={{ width: 36, height: 36 }}>{getInitials(user?.full_name)}</span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold truncate">{user?.full_name || 'User'}</span>
              <span className="block text-[11px] text-slate-500 uppercase tracking-wide">{user?.role}</span>
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); toggleTheme(); }} data-testid="fiori-theme-toggle">
            <span className="fs-menu-item">
              {isDark ? <Sun /> : <Moon />}
              <span>{isDark ? t('theme.lightMode', 'Heller Modus') : t('theme.darkMode', 'Dunkler Modus')}</span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/theme-settings')} data-testid="fiori-appearance-link">
            <span className="fs-menu-item"><Palette /><span>{t('nav.appearance', 'Erscheinungsbild')}</span></span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400 py-1">{t('fiori.language', 'Sprache')}</DropdownMenuLabel>
          <div className="flex flex-wrap gap-1 px-2 pb-2" data-testid="fiori-language-row">
            {SUPPORTED_LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => changeLanguage(l.code)}
                title={l.label}
                className={`w-8 h-8 rounded-md text-base flex items-center justify-center transition ${language === l.code ? 'ring-2 ring-[var(--fs-accent)] bg-[var(--fs-hover-bg)]' : 'hover:bg-[var(--fs-hover-bg)]'}`}
                data-testid={`fiori-lang-${l.code}`}
              >
                {l.flag}
              </button>
            ))}
          </div>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowPw(true)} data-testid="fiori-change-password">
            <span className="fs-menu-item"><KeyRound /><span>{t('auth.changePassword', 'Passwort ändern')}</span></span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowSec(true)} data-testid="fiori-security">
            <span className="fs-menu-item"><ShieldCheck /><span>{t('auth.security', 'Sicherheit')}</span></span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/my-tasks')} data-testid="fiori-my-tasks-link">
            <span className="fs-menu-item"><UserCircle /><span>{t('nav.myTasks', 'Meine Aufgaben')}</span></span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-700" data-testid="fiori-logout">
            <span className="fs-menu-item"><LogOut /><span>{t('nav.logout', 'Abmelden')}</span></span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PasswordChangeDialog isOpen={showPw} onClose={() => setShowPw(false)} />
      <SecuritySettingsDialog isOpen={showSec} onClose={() => setShowSec(false)} />
    </>
  );
}
